import React, { useContext, useState, useMemo, useEffect, Dispatch, SetStateAction } from 'react';
import MultiFilter, { StyledChip } from '../../MultiFilter';
import { LocalizationContext } from '../../LanguageContext/LocalizationContext';
import { EntityResponse, Speciality, SelectedFilters } from '../../../types';
import { BehaviorSubject, from } from 'rxjs';
import { AutocompleteGetTagProps } from '@material-ui/lab';
import { distinctUntilChanged, concatMap, map } from 'rxjs/operators';
import { specialityStore } from '../../../store/stores';
import { specialitiesToFilterOut } from "../../../settings";
import timanttiService from '../../../services/timanttiService';

interface SpecialityFilterProps {
    disabled?: boolean,
    specialityFilters: string[],
    selectedSpecialities: EntityResponse<Speciality>[],
    setSelectedFilters: Dispatch<SetStateAction<SelectedFilters>>,
    loadingInitialState: boolean
}

const filterSubject = new BehaviorSubject<string[]>([]);

let arrowKeySelectionOn: boolean = false;

const SpecialityFilter: React.FC<SpecialityFilterProps> = props => {
    const {
        disabled,
        specialityFilters,
        selectedSpecialities,
        setSelectedFilters,
        loadingInitialState
    } = props;
    const [specialityUuids, setSpecialityUuids] = useState<string[]>([]);
    const [specialities, setSpecialities] = useState<EntityResponse<Speciality>[]>([]);
    const { translation } = useContext(LocalizationContext);

    useEffect(() => {
        if (specialityFilters.length !== 0 && !loadingInitialState) {
            filterSubject.next(specialityFilters);
            const sub = filterSubject.pipe(
                distinctUntilChanged(),
                concatMap(filters => timanttiService.getSpecialities(filters)),
                map(resp => resp.data.erityisosaaminen ? resp.data.erityisosaaminen : [])
            ).subscribe(setSpecialityUuids);
            return () => sub.unsubscribe();
        }
    }, [specialityFilters, loadingInitialState]);

    useEffect(() => {
        const sub = from(specialityUuids).pipe(
            distinctUntilChanged(),
            concatMap(uuid => timanttiService.getSpeciality(uuid)
                .catch(_err => { })
            )
        ).subscribe();
        return () => sub.unsubscribe();
    }, [specialityUuids]);

    useEffect(() => {
        const sub = specialityStore.subject.subscribe(setSpecialities);
        return () => sub.unsubscribe();
    }, []);

    const onSelect = (_e: any, value: EntityResponse<Speciality>[]) => {
        setSelectedFilters((prevState: SelectedFilters) => ({
            ...prevState,
            selectedSpecialities: value
        }));
    };

    const renderTags = (
        value: EntityResponse<Speciality>[],
        getTagProps: AutocompleteGetTagProps
    ) =>
        value.map((option: EntityResponse<Speciality>, index: number) => (
            <StyledChip
                label={option.data.erityisosaaminen} {...getTagProps({ index })}
            />
        ));

    const filteredSpecialities = useMemo(() =>
        specialities
            .filter(speciality => specialityUuids.includes(speciality.uuid))
            .filter(speciality => !specialitiesToFilterOut.includes(speciality.uuid)),
        [specialities, specialityUuids]
    );

    const onBlur = (e: any) => {
        e.preventDefault();
        arrowKeySelectionOn = false;
    };

    const handleKeyDown =  (e: any) => {
        if (e.key === 'Enter') {
            if (arrowKeySelectionOn) {
                arrowKeySelectionOn = false;
                return;
            } else {
                const input = e.target.value.toLowerCase();
                if (input.length > 1) {
                    const results = specialities.filter(val =>
                        val.data.erityisosaaminen.toLowerCase().includes(input)
                    );
                    onSelect(input, results.concat(selectedSpecialities));
                }
            }
        } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            arrowKeySelectionOn = true;
        }
    };

    return (
        <MultiFilter
            disabled={disabled}
            id={"specialtyFilter"}
            options={filteredSpecialities}
            label={translation.speciality}
            getOptionLabel={speciality => speciality.data.erityisosaaminen}
            getOptionSelected={(option, value) => option.uuid === value.uuid}
            placeholder={translation.searchSpeciality}
            onChange={onSelect}
            renderTags={renderTags}
            value={selectedSpecialities}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
        />
    );
};

export default SpecialityFilter;
